import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Plus, Search } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useMarketingCampaigns } from '@/hooks/use-marketing';
import {
  PROMOTION_TYPES, promotionLabel, campaignStatusLabel, campaignStatusVariant,
  formatPeriod, isLive, type CampaignStatus, type PromotionType,
} from '@/lib/marketing';

const STATUSES: CampaignStatus[] = ['DRAFT', 'SCHEDULED', 'ACTIVE', 'PAUSED', 'COMPLETED', 'CANCELLED'];
type Timing = 'all' | 'live' | 'upcoming' | 'done';

export default function AdminMarketingCampaigns() {
  const { t, i18n } = useTranslation();
  const locale = ({ uz: 'uz-UZ', ru: 'ru-RU', en: 'en-US' } as Record<string, string>)[(i18n.language || 'uz').slice(0, 2)] ?? 'uz-UZ';
  const [type, setType] = useState<'all' | PromotionType>('all');
  const [status, setStatus] = useState<'all' | CampaignStatus>('all');
  const [timing, setTiming] = useState<Timing>('all');
  const [search, setSearch] = useState('');

  const { data: campaigns = [], isLoading } = useMarketingCampaigns({ promotion_type: type, status });

  const filtered = campaigns.filter((c: any) => {
    if (!c.name.toLowerCase().includes(search.toLowerCase())) return false;
    if (timing === 'live') return isLive(c.status, c.start_at, c.end_at);
    if (timing === 'upcoming') return new Date(c.start_at).getTime() > Date.now();
    if (timing === 'done') return ['COMPLETED', 'CANCELLED'].includes(c.status);
    return true;
  });

  return (
    <div className="mx-auto max-w-7xl animate-fade-in p-6">
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-xl font-semibold text-foreground">{t('mkt_campaigns.title')}</h1>
        <Button asChild>
          <Link to="/admin/marketing/campaigns/new"><Plus size={16} className="mr-1" /> {t('mkt_campaigns.create')}</Link>
        </Button>
      </div>

      <div className="mb-4 flex flex-wrap gap-2">
        <div className="relative min-w-[200px] flex-1">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input className="pl-9" placeholder={t('mkt_campaigns.searchPlaceholder')} value={search} onChange={(e) => setSearch(e.target.value)} />
        </div>
        <Select value={type} onValueChange={(v) => setType(v as any)}>
          <SelectTrigger className="w-[220px]"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t('mkt_campaigns.allTypes')}</SelectItem>
            {PROMOTION_TYPES.map((pt) => <SelectItem key={pt} value={pt}>{promotionLabel[pt]}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={status} onValueChange={(v) => setStatus(v as any)}>
          <SelectTrigger className="w-[180px]"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t('mkt_campaigns.allStatuses')}</SelectItem>
            {STATUSES.map((s) => <SelectItem key={s} value={s}>{campaignStatusLabel[s]}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={timing} onValueChange={(v) => setTiming(v as Timing)}>
          <SelectTrigger className="w-[170px]"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t('mkt_campaigns.allTimings')}</SelectItem>
            <SelectItem value="live">{t('mkt_campaigns.timingLive')}</SelectItem>
            <SelectItem value="upcoming">{t('mkt_campaigns.timingUpcoming')}</SelectItem>
            <SelectItem value="done">{t('mkt_campaigns.timingDone')}</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="overflow-x-auto rounded-xl border border-border bg-card">
        <table className="w-full min-w-[720px]">
          <thead>
            <tr className="border-b border-border bg-muted/50">
              <th className="p-3 text-left text-xs font-medium text-muted-foreground">{t('mkt_campaigns.colName')}</th>
              <th className="p-3 text-left text-xs font-medium text-muted-foreground">{t('mkt_campaigns.colType')}</th>
              <th className="p-3 text-left text-xs font-medium text-muted-foreground">{t('mkt_campaigns.colPeriod')}</th>
              <th className="p-3 text-left text-xs font-medium text-muted-foreground">{t('mkt_campaigns.colUpdated')}</th>
              <th className="p-3 text-left text-xs font-medium text-muted-foreground">{t('mkt_campaigns.colStatus')}</th>
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              <tr><td colSpan={5} className="p-8 text-center text-muted-foreground">{t('mkt_campaigns.loading')}</td></tr>
            ) : filtered.length === 0 ? (
              <tr>
                <td colSpan={5} className="p-8 text-center">
                  <p className="text-sm text-muted-foreground">{t('mkt_campaigns.empty')}</p>
                  <Button asChild size="sm" className="mt-3"><Link to="/admin/marketing/campaigns/new">{t('mkt_campaigns.createPlus')}</Link></Button>
                </td>
              </tr>
            ) : filtered.map((c: any) => (
              <tr key={c.id} className="border-b border-border last:border-0 hover:bg-muted/30">
                <td className="p-3">
                  <Link to={`/admin/marketing/campaigns/${c.id}`} className="text-sm font-medium text-foreground hover:underline">{c.name}</Link>
                  {isLive(c.status, c.start_at, c.end_at) && <Badge className="ml-2" variant="default">{t('mkt_campaigns.live')}</Badge>}
                </td>
                <td className="p-3 text-sm text-muted-foreground">{promotionLabel[c.promotion_type as PromotionType]}</td>
                <td className="p-3 text-sm text-muted-foreground">{formatPeriod(c.start_at, c.end_at)}</td>
                <td className="p-3 text-sm text-muted-foreground">{new Date(c.updated_at).toLocaleDateString(locale)}</td>
                <td className="p-3">
                  <Badge variant={campaignStatusVariant[c.status as CampaignStatus]}>{campaignStatusLabel[c.status as CampaignStatus]}</Badge>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
